import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { cx } from '../lib/cx.ts';

type ToastTone = 'neutral' | 'success' | 'danger';

type ToastAction = { label: string; onAction: () => void };

type ToastRequest = {
  message: string;
  tone?: ToastTone;
  /** Milliseconds. Undo affordances are 5000 by convention across the app. */
  duration?: number;
  /**
   * Kept to two at most. A save confirmation carries Undo and "Add another",
   * and anything beyond that belongs on a screen rather than in a toast.
   */
  actions?: readonly ToastAction[];
};

type Toast = ToastRequest & { id: number };

type ToastApi = {
  showToast: (toast: ToastRequest) => void;
  dismissToast: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error('useToast must be used inside a ToastProvider');
  }
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(0);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((request: ToastRequest) => {
    nextId.current += 1;
    setToasts((current) => [...current, { ...request, id: nextId.current }]);
  }, []);

  const api = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        // `polite` rather than `assertive`: a save confirmation should not
        // interrupt whatever the user is doing next.
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 px-4 pb-(--ui-float-clearance) rail:items-start rail:pl-[calc(var(--ui-rail-width)+1rem)] rail:pb-[calc(var(--ui-fab-height)+2.5rem)]"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONES: Record<ToastTone, string> = {
  neutral: 'border-line',
  success: 'border-success',
  danger: 'border-danger',
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const { id, duration = 5000 } = toast;

  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(id), duration);
    return () => window.clearTimeout(timer);
  }, [id, duration, onDismiss]);

  return (
    <div
      className={cx(
        'pointer-events-auto flex w-full max-w-content items-center justify-between gap-4',
        'rounded-md border bg-surface-raised px-4 py-3 shadow-md',
        TONES[toast.tone ?? 'neutral'],
      )}
    >
      <p className="text-sm text-text">{toast.message}</p>
      {toast.actions === undefined ? null : (
        <div className="flex shrink-0 items-center gap-1">
          {toast.actions.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={() => {
                action.onAction();
                onDismiss(id);
              }}
              className="min-h-touch shrink-0 px-2 text-sm font-strong text-accent hover:text-accent-hover"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
